import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  Power,
  RefreshCw,
  Save,
  Trash2,
  VideoOff,
  WifiOff,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useDeviceFrame } from "@/hooks/useDeviceFrame";
import { toast } from "sonner";
import dayjs from "dayjs";

function displayState(camera, signal, src) {
  if (!camera?.enabled) return { label: "Désactivée", dot: "bg-zinc-500", icon: VideoOff };
  if (camera.runtime_status !== "online") return { label: "Hors ligne", dot: "bg-red-500", icon: WifiOff };
  if (signal === "waiting" || !src) return { label: "Connexion…", dot: "bg-amber-400", icon: null };
  if (signal === "lost") return { label: "Hors ligne", dot: "bg-red-500", icon: WifiOff };
  return { label: "En ligne", dot: "bg-emerald-500", icon: null };
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-1 py-2.5 text-sm sm:flex-row sm:items-start sm:justify-between sm:gap-5">
      <span className="shrink-0 text-zinc-500">{label}</span>
      <span className="break-all text-left text-zinc-200 sm:text-right">{value || "—"}</span>
    </div>
  );
}

export default function CameraSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [camera, setCamera] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = async ({ syncName = false, silent = false } = {}) => {
    try {
      const response = await api.get(`/cameras/${id}`);
      setCamera(response.data);
      if (syncName) setName(response.data.name);
      return response.data;
    } catch (error) {
      if (!silent) toast.error(formatApiError(error));
      if (error?.response?.status === 404) navigate("/cameras", { replace: true });
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    refresh({ syncName: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const timer = setInterval(() => refresh({ silent: true }), 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const active = Boolean(camera?.enabled && camera?.runtime_status === "online");
  const { src, signal, ratio } = useDeviceFrame({ cameraId: camera?.id, active, pollMs: 120 });
  const state = displayState(camera, signal, src);
  const StateIcon = state.icon;
  const metadata = camera?.source_metadata || {};

  const saveName = async () => {
    const cleaned = name.trim();
    if (!camera || cleaned === camera.name) return;
    if (cleaned.length < 3 || cleaned.length > 60) {
      toast.error("Le nom doit contenir entre 3 et 60 caractères.");
      return;
    }
    setSaving(true);
    try {
      const response = await api.patch(`/cameras/${camera.id}`, { name: cleaned });
      setCamera(response.data);
      setName(response.data.name);
      toast.success("Nom mis à jour");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (!camera) return;
    setSaving(true);
    try {
      const response = await api.patch(`/cameras/${camera.id}`, { enabled: !camera.enabled });
      setCamera(response.data);
      setTestResult(null);
      toast.success(response.data.enabled ? "Caméra activée" : "Caméra désactivée");
      if (response.data.enabled) setTimeout(() => refresh({ silent: true }), 1500);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!camera) return;
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post(`/cameras/${camera.id}/test`);
      setTestResult(response.data);
      if (response.data.ok) toast.success("Connexion réussie");
      else toast.error(response.data.message || "Connexion impossible");
      await refresh({ silent: true });
    } catch (error) {
      const message = formatApiError(error);
      setTestResult({ ok: false, message });
      toast.error(message);
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!camera) return;
    setSaving(true);
    try {
      await api.delete(`/cameras/${camera.id}`);
      toast.success("Caméra supprimée");
      navigate("/cameras", { replace: true });
    } catch (error) {
      toast.error(formatApiError(error));
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  if (loading || !camera) {
    return <div className="px-4 py-10 text-sm text-zinc-500 animate-pulse sm:px-6 lg:px-8">Chargement…</div>;
  }

  const rtspPort = metadata.rtsp_port || (camera.source_type === "ip" ? 554 : null);
  const onvifPort = metadata.onvif_port;
  const detectedVia = metadata.detected_via === "onvif"
    ? "ONVIF"
    : metadata.detected_via === "manual_rtsp"
      ? "RTSP manuel"
      : metadata.detected_via === "rtsp_common_path"
        ? "RTSP automatique"
        : metadata.detected_via;

  return (
    <div data-testid="camera-settings-page">
      <PageHeader
        title="Paramètres de la caméra"
        description={camera.name}
        actions={
          <Button variant="outline" onClick={() => navigate("/cameras")} className="h-11 rounded-xl border-white/[0.10] bg-transparent text-zinc-300 hover:bg-white/[0.05] hover:text-white" data-testid="camera-settings-back">
            <ChevronLeft className="mr-1 size-4" strokeWidth={1.7} /> Caméras
          </Button>
        }
      />

      <div className="grid max-w-6xl gap-5 px-4 pb-10 sm:px-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)] lg:px-8">
        <div className="space-y-5">
          <section className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#111114]">
            <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: ratio ? String(ratio) : "16 / 9" }}>
              {state.label === "En ligne" && src ? (
                <img src={src} alt={camera.name} className="absolute inset-0 h-full w-full object-contain" data-testid="camera-settings-preview" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-500">
                  {StateIcon ? <StateIcon className="size-9 text-zinc-700" strokeWidth={1.3} /> : null}
                  <span className={state.label === "Connexion…" ? "animate-pulse" : ""}>{state.label}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-start gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">{camera.name}</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-zinc-400">
                  <span className={`size-1.5 rounded-full ${state.dot}`} /> {state.label}
                </div>
              </div>
              <div className="break-all text-left text-xs text-zinc-500 sm:text-right">{camera.address || "Caméra de cet appareil"}</div>
            </div>
          </section>

          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-zinc-200">Caméra</h2>
            <div className="mt-4">
              <label htmlFor="camera-name" className="mb-2 block text-xs font-medium text-zinc-400">Nom</label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input id="camera-name" value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") saveName(); }} maxLength={60} className="h-11 rounded-xl border-white/[0.10] bg-black/20 text-zinc-100" data-testid="camera-name-input" />
                <Button onClick={saveName} disabled={saving || name.trim() === camera.name} className="h-11 w-full rounded-xl bg-brand hover:bg-brand-hover sm:w-auto" data-testid="camera-name-save">
                  <Save className="mr-1.5 size-4" strokeWidth={1.7} /> Enregistrer
                </Button>
              </div>
            </div>
            <div className="mt-3 divide-y divide-white/[0.06]">
              <InfoRow label="État" value={state.label} />
              <InfoRow label="Adresse" value={camera.address} />
            </div>
          </section>

          <details className="group rounded-2xl border border-white/[0.08] bg-white/[0.025]" data-testid="camera-advanced-settings">
            <summary className="cursor-pointer list-none px-4 py-4 text-sm font-semibold text-zinc-300 sm:px-5 [&::-webkit-details-marker]:hidden">
              <span className="flex items-center justify-between gap-3">
                Paramètres avancés
                <span className="text-xs font-normal text-zinc-600 transition group-open:rotate-180">⌄</span>
              </span>
            </summary>
            <div className="border-t border-white/[0.06] px-4 py-3 sm:px-5">
              <InfoRow label="Type" value={camera.source_type === "ip" ? "Caméra IP" : "Caméra appareil"} />
              <InfoRow label="Protocole" value={camera.protocol} />
              <InfoRow label="Détection" value={detectedVia} />
              <InfoRow label="Port RTSP" value={rtspPort ? String(rtspPort) : null} />
              <InfoRow label="Chemin RTSP" value={metadata.path} />
              <InfoRow label="Port ONVIF" value={onvifPort ? String(onvifPort) : null} />
              <InfoRow label="Profil ONVIF" value={metadata.profile_name || metadata.profile_token} />
              <InfoRow label="Profil CAM" value={camera.profile} />
              <InfoRow label="Codec" value={metadata.codec} />
              <InfoRow label="Résolution" value={metadata.resolution} />
              <InfoRow label="FPS" value={metadata.fps ? String(metadata.fps) : null} />
              <InfoRow label="Dernière image" value={camera.last_frame_at ? dayjs(camera.last_frame_at).format("DD/MM/YYYY HH:mm:ss") : null} />
            </div>
          </details>
        </div>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-zinc-200">Actions</h2>
            <div className="mt-4 space-y-2.5">
              <Button variant="outline" onClick={testConnection} disabled={testing} className="h-11 w-full justify-start rounded-xl border-white/[0.10] bg-transparent text-zinc-200 hover:bg-white/[0.05] hover:text-white" data-testid="camera-test-connection">
                <RefreshCw className={`mr-2 size-4 ${testing ? "animate-spin" : ""}`} strokeWidth={1.7} />
                {testing ? "Test en cours…" : "Tester la connexion"}
              </Button>
              <Button variant="outline" onClick={toggleEnabled} disabled={saving} className="h-11 w-full justify-start rounded-xl border-white/[0.10] bg-transparent text-zinc-200 hover:bg-white/[0.05] hover:text-white" data-testid="camera-toggle-enabled">
                <Power className="mr-2 size-4" strokeWidth={1.7} />
                {camera.enabled ? "Désactiver la caméra" : "Activer la caméra"}
              </Button>
            </div>

            {testResult && (
              <div className={`mt-4 rounded-xl border px-3 py-2.5 text-sm ${testResult.ok ? "border-emerald-500/20 bg-emerald-500/[0.06] text-emerald-300" : "border-red-500/20 bg-red-500/[0.06] text-red-300"}`} data-testid="camera-test-result">
                {testResult.message}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-red-500/15 bg-red-500/[0.025] p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-zinc-200">Supprimer</h2>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">Retire cette caméra de CAM. Cette action est définitive.</p>
            {!confirmDelete ? (
              <Button variant="outline" onClick={() => setConfirmDelete(true)} className="mt-4 h-11 w-full justify-start rounded-xl border-red-500/25 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="camera-delete">
                <Trash2 className="mr-2 size-4" strokeWidth={1.7} /> Supprimer la caméra
              </Button>
            ) : (
              <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/[0.05] p-3" data-testid="camera-delete-confirmation">
                <div className="flex items-start gap-2 text-sm text-red-200">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.7} />
                  Confirmer la suppression de « {camera.name} » ?
                </div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={saving} className="h-11 flex-1 rounded-lg border-white/[0.10] bg-transparent">Annuler</Button>
                  <Button onClick={remove} disabled={saving} className="h-11 flex-1 rounded-lg bg-red-600 text-white hover:bg-red-500" data-testid="camera-delete-confirm">Supprimer</Button>
                </div>
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
